import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Observable } from 'rxjs';

import { ResourceEmployeeMembership, ResourceEmployeeProfile, ResourceTeam } from '../../../../Models/resource-modules.models';
import { ResourceKnowledge } from '../../../../Models/resource.models';
import { ConfirmDialogComponent, ConfirmDialogData } from '../../../shared/confirm-dialog/confirm-dialog.component';
import { ResourceTeamManagementStore } from '../resource-team-management.store';

/**
 * Mitarbeiterprofil im Seitenpanel: Teams und Hauptteam (Zuordnung über dieselben
 * Mitglieder-Aktionen wie die Teamansicht), Gesamtplanung (automatisch über Team oder
 * manuelle Aufnahme), Wochenkapazität und Skills.
 */
@Component({
  selector: 'app-resource-employee-profile',
  templateUrl: './resource-employee-profile.component.html',
  styleUrls: ['../resource-team-management.shared.css', './resource-employee-profile.component.css']
})
export class ResourceEmployeeProfileComponent implements OnChanges {
  @Input() userId!: string;
  @Output() closed = new EventEmitter<void>();
  @Output() openTeam = new EventEmitter<number>();

  weeklyHours = 38.5;
  /** Vom Benutzer pflegbare Skills (Teamleitung: global + eigene Teamkataloge). */
  editableSkillIds: number[] = [];
  addTeamId: number | null = null;
  skillSearch = '';

  constructor(
    public store: ResourceTeamManagementStore,
    private dialog: MatDialog,
    private snackBar: MatSnackBar
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userId']) {
      this.resetForm();
    }
  }

  get profile(): ResourceEmployeeProfile | undefined {
    const profile = this.store.profile(this.userId);
    return profile;
  }

  private resetForm(): void {
    const profile = this.store.profile(this.userId);
    this.addTeamId = null;
    this.skillSearch = '';
    if (!profile) return;
    this.weeklyHours = profile.weeklyCapacityHours;
    const editable = new Set(this.store.assignableSkills.map(s => s.id));
    this.editableSkillIds = profile.skillIds.filter(id => editable.has(id));
  }

  // ---- Anzeige / Rechte ---------------------------------------------------------------

  get canEditCore(): boolean { return this.store.canEditProfiles; }
  get canEditSkills(): boolean { return this.store.canEditSkillsOf(this.userId); }

  get dailyHours(): number { return Math.round((this.weeklyHours / 5) * 100) / 100; }

  /** Skills, die nicht gepflegt werden dürfen (z. B. aus fremden Teamkatalogen) – bleiben unverändert. */
  get fixedSkills(): ResourceKnowledge[] {
    const profile = this.profile;
    if (!profile) return [];
    const editable = new Set(this.store.assignableSkills.map(s => s.id));
    return this.store.skills.filter(s => profile.skillIds.includes(s.id) && (!this.canEditSkills || !editable.has(s.id)));
  }

  get skillOptions(): ResourceKnowledge[] {
    const q = this.skillSearch.trim().toLocaleLowerCase('de');
    return this.store.assignableSkills.filter(s =>
      this.editableSkillIds.includes(s.id) || !q || s.name.toLocaleLowerCase('de').includes(q));
  }

  get selectedSkills(): ResourceKnowledge[] {
    return this.store.skills.filter(s => this.editableSkillIds.includes(s.id));
  }

  get dirty(): boolean {
    const p = this.profile;
    if (!p) return false;
    const editable = new Set(this.store.assignableSkills.map(s => s.id));
    const current = p.skillIds.filter(id => editable.has(id)).sort().join(',');
    return Math.abs(p.weeklyCapacityHours - this.weeklyHours) > 0.0001 ||
      current !== [...this.editableSkillIds].sort().join(',');
  }

  get memberships(): ResourceEmployeeMembership[] { return this.profile?.memberships ?? []; }

  get leadsAnyTeam(): boolean { return this.memberships.some(m => m.isLeader); }

  /** In der Gesamtplanung ist, wer einem aktiven Team angehört ODER manuell aufgenommen wurde. */
  get inPlanning(): boolean { return !!this.profile?.isInPlanning; }
  get plannedByTeam(): boolean { return !!this.profile?.isPlannedByTeam; }
  get manuallyPlanned(): boolean { return !!this.profile?.isManuallyPlanned; }

  /** Manuell aufnehmen bzw. die manuelle Aufnahme aufheben: SuperAdmin und Kaufmännisch. */
  get canManagePlanning(): boolean { return this.store.canManagePlanningMembers; }

  get planningStatusText(): string {
    if (this.plannedByTeam && this.manuallyPlanned) return 'In der Gesamtplanung (über Team und manuell)';
    if (this.plannedByTeam) return 'In der Gesamtplanung (über Team)';
    if (this.manuallyPlanned) return 'In der Gesamtplanung (manuell aufgenommen)';
    return 'Nicht in der Planung';
  }

  // ---- Gesamtplanung: manuelle Aufnahme unabhängig von Teams --------------------------

  addToPlanning(): void {
    if (!this.canManagePlanning || this.manuallyPlanned || this.store.busy) return;
    this.handle(this.store.addToPlanning([this.userId]), `${this.profile?.name} ist jetzt in der Gesamtplanung.`);
  }

  removeFromPlanning(): void {
    if (!this.canManagePlanning || !this.manuallyPlanned || this.store.busy) return;
    const teamNames = this.memberships.map(m => m.teamName);
    this.confirm({
      title: 'Aus der Gesamtplanung entfernen',
      message: `Manuelle Aufnahme von ${this.profile?.name} in die Gesamtplanung aufheben?`,
      details: [
        'Benutzerkonto, Termine, Abwesenheiten und Kapazitätsdaten bleiben erhalten.',
        ...(teamNames.length
          ? [`Bleibt über ${teamNames.length === 1 ? 'das Team' : 'die Teams'} ${teamNames.join(', ')} weiterhin in der Gesamtplanung.`]
          : ['Ohne Team erscheint der Mitarbeiter danach nicht mehr in der Gesamtplanung.'])
      ],
      confirmText: 'Entfernen'
    }, () => this.handle(this.store.removeFromPlanning(this.userId), 'Manuelle Aufnahme aufgehoben.'));
  }

  teamOf(membership: ResourceEmployeeMembership): ResourceTeam | undefined {
    return this.store.team(membership.teamId);
  }

  canRemoveFrom(membership: ResourceEmployeeMembership): boolean {
    return !membership.isLeader && this.store.canManageTeam(this.teamOf(membership));
  }

  /** Teams, denen der Benutzer diesen Mitarbeiter zuordnen darf. */
  get addableTeams(): ResourceTeam[] {
    const current = new Set(this.memberships.map(m => m.teamId));
    return this.store.manageableTeams.filter(t => !current.has(t.id));
  }

  get canChoosePrimary(): boolean {
    return this.memberships.length > 1 && this.store.canSetPrimaryFor(this.userId);
  }

  get primaryName(): string {
    return this.memberships.find(m => m.teamId === this.profile?.primaryTeamId)?.teamName ?? '';
  }

  // ---- Teamzuordnung (gleiche Aktionen wie in der Teamansicht) ------------------------

  addToTeam(): void {
    if (this.addTeamId === null || this.store.busy) return;
    const team = this.store.team(this.addTeamId);
    this.handle(this.store.addMembers(this.addTeamId, [this.userId]),
      `Dem Team „${team?.name}“ zugeordnet.`, () => this.addTeamId = null);
  }

  removeFromTeam(membership: ResourceEmployeeMembership): void {
    if (!this.canRemoveFrom(membership) || this.store.busy) return;
    const remaining = this.memberships.length - 1;
    this.confirm({
      title: 'Aus Team entfernen',
      message: `${this.profile?.name} aus dem Team „${membership.teamName}“ entfernen?`,
      details: [
        'Bestehende Planungstermine bleiben erhalten.',
        ...(remaining === 1 ? ['Das verbleibende Team wird automatisch zum Hauptteam.'] : []),
        ...(remaining === 0 && !this.manuallyPlanned ? ['Ohne Team und ohne manuelle Aufnahme erscheint der Mitarbeiter nicht mehr in der Gesamtplanung.'] : []),
        ...(remaining === 0 && this.manuallyPlanned ? ['Bleibt durch die manuelle Aufnahme in der Gesamtplanung.'] : []),
        ...(remaining === 0 ? ['Ohne Team muss ein Urlaubsgenehmiger manuell festgelegt sein.'] : []),
        ...(remaining > 1 && membership.teamId === this.profile?.primaryTeamId
          ? ['Danach muss ein neues Hauptteam gewählt werden.'] : [])
      ],
      confirmText: 'Entfernen'
    }, () => this.handle(this.store.removeMember(membership.teamId, this.userId), 'Aus dem Team entfernt.'));
  }

  setPrimary(teamId: number): void {
    if (!this.canChoosePrimary || teamId === this.profile?.primaryTeamId || this.store.busy) return;
    const name = this.memberships.find(m => m.teamId === teamId)?.teamName;
    this.handle(this.store.setPrimaryTeam(teamId, this.userId), `Hauptteam: ${name}.`);
  }

  // ---- Profil speichern ---------------------------------------------------------------

  save(): void {
    const profile = this.profile;
    if (!profile || !this.dirty || this.store.busy) return;
    if (this.canEditCore && (!Number.isFinite(this.weeklyHours) || this.weeklyHours <= 0 || this.weeklyHours > 168)) {
      this.snackBar.open('Bitte gültige Wochenstunden (über 0 bis 168) eingeben.', 'OK', { duration: 4000 });
      return;
    }
    const payload = {
      weeklyCapacityHours: this.canEditCore ? this.weeklyHours : profile.weeklyCapacityHours,
      // Nicht pflegbare Skills bleiben erhalten.
      skillIds: [...new Set([...this.fixedSkills.map(s => s.id), ...this.editableSkillIds])]
    };
    this.handle(this.store.saveProfile(this.userId, payload), 'Profil gespeichert.', () => this.resetForm());
  }

  discard(): void {
    this.resetForm();
  }

  removeSkill(id: number): void {
    this.editableSkillIds = this.editableSkillIds.filter(x => x !== id);
  }

  // ---- Hilfen -----------------------------------------------------------------------

  private handle(request: Observable<unknown>, message: string, done?: () => void): void {
    request.subscribe({
      next: () => { done?.(); this.snackBar.open(message, 'OK', { duration: 3000 }); },
      error: (err: HttpErrorResponse) => this.snackBar.open(
        ResourceTeamManagementStore.errorText(err, 'Die Aktion ist fehlgeschlagen.'), 'OK', { duration: 5000 })
    });
  }

  private confirm(data: ConfirmDialogData, onConfirm: () => void): void {
    this.dialog.open(ConfirmDialogComponent, { data, width: '460px', maxWidth: '95vw' })
      .afterClosed()
      .subscribe(ok => { if (ok) onConfirm(); });
  }
}
